import React from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {ChangePassword, IChangePassword, IPasswordData} from 'types/PasswordData';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import Panel from 'components/Panel';
import FormikTextField from 'components/inputs/FormikTextField';
import Button from 'components/Button';
import {useTheme} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorRoundedIcon from '@mui/icons-material/ClearRounded';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {useSelector} from 'react-redux';
import {licenseSelectors} from 'redux/license';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {enums} from 'types/enums';
import {calcNextTime} from 'common/dateUtils';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';

type ChangePasswordFormProps = {
   onSubmit: (values: IChangePassword, formikHelpers: FormOnSubmitHelpers<IChangePassword>) => void;
   fromUserAdmin?: boolean;
   fromStartup?: boolean;
   passwordData: IPasswordData;
   onClose: () => void;
   lastPasswordUpdate?: Date | number | string;
};

const Form = ({
   onClose,
   fromUserAdmin,
   fromStartup,
   lastPasswordUpdate,
}: Omit<ChangePasswordFormProps, 'onSubmit' | 'passwordData'>) => {
   const {t} = useTranslation();
   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const getValue = useFieldValueGetter<IChangePassword>();

   const newPassword = getValue('newPassword');
   const currentPassword = getValue('currentPassword');
   const newPassword2 = getValue('newPassword2');
   const passwordData = getValue('passwordData') || ({} as IPasswordData);

   let isCurrentDoNotMatch = false,
      isMixedCheck = false,
      isAlphaNumericCheck = false,
      isSpecialCharCheck = false,
      isValid = true;

   if (!fromUserAdmin) {
      isCurrentDoNotMatch = currentPassword !== newPassword;
      isValid = isValid && isCurrentDoNotMatch;
   }
   if (passwordData.alphaNumericRequired) {
      isAlphaNumericCheck = /[a-zA-Z]+/.test(newPassword) && /[0-9]+/.test(newPassword);
      isValid = isValid && isAlphaNumericCheck;
   }
   if (passwordData.mixedCaseRequired) {
      isMixedCheck = /[a-z]+/.test(newPassword) && /[A-Z]+/.test(newPassword);
      isValid = isValid && isMixedCheck;
   }
   if (passwordData.specialCharsRequired) {
      isSpecialCharCheck = /[^a-zA-Z0-9]+/.test(newPassword);
      isValid = isValid && isSpecialCharCheck;
   }
   const isLengthCheck = !!newPassword && newPassword.length >= +passwordData.minNumChars;
   const isNewMatched = newPassword === newPassword2;
   isValid = isValid && isLengthCheck && isNewMatched;

   return (
      <>
         <Panel>
            <Grid container spacing={1} alignItems={'center'}>
               <Grid item xs={6}>
                  {!fromUserAdmin ? (
                     <FormikTextField
                        autoFocus={!fromUserAdmin}
                        type="password"
                        name="currentPassword"
                        label={t('Current Password')}
                     />
                  ) : null}
                  <FormikTextField
                     autoFocus={fromUserAdmin}
                     type="password"
                     name="newPassword"
                     label={t('New Password')}
                  />
                  <FormikTextField
                     type="password"
                     name="newPassword2"
                     label={t('Confirm New Password')}
                  />
               </Grid>
               <Grid item xs={6}>
                  <Panel name={t('Password Requirements')}>
                     {!fromUserAdmin ? (
                        <ValidationEntry
                           valid={isCurrentDoNotMatch}
                           label={t('Cannot match current password')}
                        />
                     ) : null}
                     <ValidationEntry
                        valid={isLengthCheck}
                        label={
                           <Trans t={t}>
                              Must have at least {{minNumChars: passwordData.minNumChars}}{' '}
                              characters
                           </Trans>
                        }
                     />
                     {passwordData.mixedCaseRequired ? (
                        <ValidationEntry
                           valid={isMixedCheck}
                           label={t('Must have both uppercase and lowercase characters')}
                        />
                     ) : null}
                     {passwordData.alphaNumericRequired ? (
                        <ValidationEntry
                           valid={isAlphaNumericCheck}
                           label={t('Must have both alpha and numeric characters')}
                        />
                     ) : null}
                     {passwordData.specialCharsRequired ? (
                        <ValidationEntry
                           valid={isSpecialCharCheck}
                           label={t('Must contain at least 1 special character')}
                        />
                     ) : null}
                     <ValidationEntry
                        valid={isNewMatched}
                        label={t('Confirmation must match new password')}
                     />
                  </Panel>
               </Grid>
            </Grid>
            {lastPasswordUpdate && <LastUpdatedLabel date={lastPasswordUpdate} />}
         </Panel>
         {!fromStartup && !isFeatureSupported(DiplomatFeature.USER_ADMIN) && (
            <Panel name={t('Change Update Interval')}>
               <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                     <FormikDropdown
                        name="passwordData.passwordUpdateInterval"
                        placeholder={t('Minimum password change frequency')}
                        options={enums.passwordUpdateInterval.options}
                     />
                  </Grid>
               </Grid>
               {lastPasswordUpdate &&
                  passwordData?.passwordUpdateInterval &&
                  passwordData?.passwordUpdateInterval !==
                     enums.passwordUpdateInterval.enum.NONE && (
                     <Grid container>
                        <Grid item>
                           <Box paddingY={1}>
                              {t('Password Change Required by {{time}}', {
                                 time: calcNextTime(
                                    passwordData.passwordUpdateInterval,
                                    lastPasswordUpdate
                                 ),
                              })}
                           </Box>
                        </Grid>
                     </Grid>
                  )}
               <LastUpdatedLabel date={passwordData.timestamp} />
            </Panel>
         )}
         <Grid container justifyContent="center" spacing={2}>
            <Grid item>
               <PrimaryButton type={'submit'} disabled={!isValid}>
                  {t('Save')}
               </PrimaryButton>
            </Grid>
            <Grid item>
               <Button type="button" onClick={onClose}>
                  {t('Cancel')}
               </Button>
            </Grid>
         </Grid>
      </>
   );
};

export const ChangePasswordForm = ({
   onSubmit,
   passwordData,
   ...otherProps
}: ChangePasswordFormProps) => {
   return (
      <Formik
         initialValues={
            ChangePassword.cast({
               passwordData,
            }) as IChangePassword
         }
         validationSchema={ChangePassword}
         onSubmit={onSubmit}
      >
         <Form {...otherProps} />
      </Formik>
   );
};

type ValidationEntryProps = {
   valid: boolean;
   label: React.ReactNode;
};

function ValidationEntry({valid, label}: ValidationEntryProps) {
   const theme = useTheme();
   const IconComponent = valid ? CheckRoundedIcon : ErrorRoundedIcon;
   return (
      <Box display={'flex'}>
         <IconComponent
            style={{color: valid ? theme.palette.success.main : theme.palette.error.main}}
         />
         <Typography>{label}</Typography>
      </Box>
   );
}
